import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export default function ColumnManagerModal({ open, onClose, columns, setColumns, defaultColumns }) {
  const [localCols, setLocalCols] = useState(columns);

  useEffect(() => {
    setLocalCols(columns);
  }, [columns]);

  if (!open) return null;

  const updateCol = (idx, changes) => {
    setLocalCols(prev => {
      const copy = [...prev];
      const updated = { ...copy[idx], ...changes };
      copy[idx] = updated;
      return copy;
    });
  };

  const move = (from, to) => {
    if (to < 0 || to >= localCols.length) return;
    setLocalCols(prev => {
      const copy = [...prev];
      copy.splice(to, 0, copy.splice(from, 1)[0]);
      return copy;
    });
  };

  const addColumn = () => {
    const key = `custom_${Date.now()}`;
    setLocalCols(prev => [...prev, { key, label: 'New Column', editable: true }]);
  };

  const removeColumn = (idx) => {
    setLocalCols(prev => prev.filter((_, i) => i !== idx));
  };

  const save = () => {
    setColumns(localCols);
    onClose();
  };

  const reset = () => {
    setColumns(defaultColumns);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-3xl mx-4 p-6 rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Customize Columns</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X/></button>
        </div>
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left bg-gray-100">
              <th className="p-2">Visible</th>
              <th className="p-2">Label</th>
              <th className="p-2">Type</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {localCols.map((col, idx) => (
              <tr key={col.key} className="border-b">
                <td className="p-2">
                  <input type="checkbox" checked={!col.hidden} onChange={e=>updateCol(idx,{hidden:!e.target.checked})}/>
                </td>
                <td className="p-2">
                  <input className="border px-2 py-1 w-full" value={col.label} onChange={e=>updateCol(idx,{label:e.target.value})}/>
                </td>
                <td className="p-2">
                  <select className="border px-2 py-1 w-full" value={col.type || 'text'} onChange={e=>updateCol(idx,{type:e.target.value})}>
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="select">Select</option>
                    <option value="date">Date</option>
                    <option value="number">Number</option>
                  </select>
                </td>
                <td className="p-2 flex gap-1">
                  <button onClick={()=>move(idx,idx-1)} className="text-gray-600 hover:text-gray-800"><ChevronUp size={16}/></button>
                  <button onClick={()=>move(idx,idx+1)} className="text-gray-600 hover:text-gray-800"><ChevronDown size={16}/></button>
                  <button onClick={()=>removeColumn(idx)} className="text-red-600 hover:text-red-800"><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between">
          <button onClick={addColumn} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded text-sm"><Plus size={16}/>Add Column</button>
          <div className="space-x-2">
            <button onClick={reset} className="bg-gray-500 text-white px-3 py-2 rounded text-sm">Reset Defaults</button>
            <button onClick={save} className="bg-green-600 text-white px-3 py-2 rounded text-sm">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
} 